import { createContext, useState, useContext, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { getDiaryRoomFromDatabase, saveDiaryRoomToDatabase } from "../hooks/api";

const DiaryRoomContext = createContext();

export const DiaryRoomProvider = ({ children }) => {
  const [diaryRoomData, setDiaryRoomData] = useState(() => {
    const stored = localStorage.getItem("childDiaryRoom");
    return stored
      ? JSON.parse(stored)
      : { itemsArray: [], itemPositions: {} };
  });

  const isHydratedRef = useRef(false);

  const loadDiaryRoom = useRef((childId) => {
    isHydratedRef.current = false;
    getDiaryRoomFromDatabase(childId)
      .then((data) => {
        const fresh = {
          itemsArray:    data.diary_items          ?? [],
          itemPositions: data.diary_item_positions ?? {},
        };
        // Confiar siempre en lo que devuelve el backend (aunque venga vacío) — si no, un
        // niño con el cuarto realmente vacío hereda la decoración cacheada de otro niño que
        // se logueó antes en el mismo dispositivo (misma key de localStorage sin child_id).
        setDiaryRoomData(fresh);
        localStorage.setItem("childDiaryRoom", JSON.stringify(fresh));
      })
      .catch((err) => console.error("No se pudo cargar el cuarto del diario:", err))
      .finally(() => { isHydratedRef.current = true; });
  });

  useEffect(() => {
    const childId = localStorage.getItem("childId");
    if (!childId) return;
    loadDiaryRoom.current(childId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStorage.getItem("childId")]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== "childId") return;
      if (e.newValue) {
        loadDiaryRoom.current(e.newValue);
      } else {
        isHydratedRef.current = false;
        setDiaryRoomData({ itemsArray: [], itemPositions: {} });
        localStorage.removeItem("childDiaryRoom");
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    localStorage.setItem("childDiaryRoom", JSON.stringify(diaryRoomData));
    if (!isHydratedRef.current) return;
    const childId = localStorage.getItem("childId");
    if (!childId) return;
    saveDiaryRoomToDatabase(diaryRoomData.itemsArray, diaryRoomData.itemPositions || {})
      .catch((err) => console.error("Error al guardar el cuarto del diario:", err));
  }, [diaryRoomData]);

  const value = useMemo(() => ({ diaryRoomData, setDiaryRoomData }), [diaryRoomData]);

  return (
    <DiaryRoomContext.Provider value={value}>
      {children}
    </DiaryRoomContext.Provider>
  );
};

DiaryRoomProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useDiaryRoom = () => useContext(DiaryRoomContext);
